import React, { useEffect, useMemo, useState } from "react";
import "../styles/Admin.css";
import {
  listMeters,
  createMeter,
  updateMeter,
  listSessions,
  openSession,
  recordSession,
  closeSession,
} from "../services/adminMeteredApi";

function fmtTs(v) {
  if (!v) return "-";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleString();
  } catch {
    return String(v);
  }
}

function toNumOrEmpty(v) {
  if (v === "" || v === null || v === undefined) return "";
  const n = Number(v);
  return Number.isNaN(n) ? "" : n;
}

export default function AdminMeteredPage({ adminEmail, onLogout }) {
  const [loading, setLoading] = useState(false);
  const [busyKey, setBusyKey] = useState("");
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");

  // ----- Meters
  const [meters, setMeters] = useState([]);
  const [meterFilters, setMeterFilters] = useState({
    branchId: "",
    active: "",
    type: "",
  });

  const [meterForm, setMeterForm] = useState({
    branchId: "",
    locationId: "",
    name: "",
    type: "fuel",
    uom: "litre",
    isActive: true,
    metaText: "",
  });

  const metersById = useMemo(() => {
    const m = new Map();
    (meters || []).forEach((x) => m.set(String(x.id), x));
    return m;
  }, [meters]);

  // ----- Sessions
  const [sessions, setSessions] = useState([]);
  const [sessionFilters, setSessionFilters] = useState({
    branchId: "",
    meterId: "",
    status: "",
    limit: 100,
    offset: 0,
  });

  const [openForm, setOpenForm] = useState({
    meterId: "",
    branchId: "",
    startReading: "",
    unitPrice: "",
    taxRate: "",
    notes: "",
  });

  const [editBySessionId, setEditBySessionId] = useState({}); // { [id]: { endReading, quantityDispensed, unitPrice, taxRate } }

  async function loadMeters() {
    const params = {
      branchId: meterFilters.branchId || undefined,
      type: meterFilters.type || undefined,
      active:
        meterFilters.active === ""
          ? undefined
          : meterFilters.active === "1"
          ? 1
          : 0,
      limit: 200,
      offset: 0,
    };

    const res = await listMeters(params).catch(() => null);
    if (!res?.ok) throw new Error(res?.message || res?.error || "Failed to load meters");

    const rows = res.rows ?? res.meters ?? [];
    setMeters(Array.isArray(rows) ? rows : []);
  }

  async function loadSessions() {
    const params = {
      branchId: sessionFilters.branchId || undefined,
      meterId: sessionFilters.meterId || undefined,
      status: sessionFilters.status || undefined,
      limit: sessionFilters.limit ?? 100,
      offset: sessionFilters.offset ?? 0,
    };

    const res = await listSessions(params).catch(() => null);
    if (!res?.ok) throw new Error(res?.message || res?.error || "Failed to load sessions");

    const rows = res.rows ?? res.sessions ?? [];
    setSessions(Array.isArray(rows) ? rows : []);
  }

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError("");
        setOkMsg("");
        await loadMeters();
        await loadSessions();
      } catch (e) {
        if (mounted) setError(e?.message || "Failed to load");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function refreshAll() {
    setError("");
    setOkMsg("");
    try {
      setLoading(true);
      await loadMeters();
      await loadSessions();
    } catch (e) {
      setError(e?.message || "Refresh failed");
    } finally {
      setLoading(false);
    }
  }

  async function onCreateMeter(e) {
    e.preventDefault();
    setError("");
    setOkMsg("");

    const payload = {
      branchId: Number(meterForm.branchId),
      locationId: meterForm.locationId ? Number(meterForm.locationId) : null,
      name: meterForm.name.trim(),
      type: meterForm.type.trim(),
      uom: meterForm.uom.trim(),
      isActive: Boolean(meterForm.isActive),
      meta: null,
    };

    if (meterForm.metaText.trim()) {
      try {
        payload.meta = JSON.parse(meterForm.metaText.trim());
      } catch {
        setError("Meta must be valid JSON.");
        return;
      }
    }

    const key = "create-meter";
    setBusyKey(key);

    try {
      const res = await createMeter(payload).catch(() => null);
      if (!res?.ok) throw new Error(res?.message || res?.error || "Create meter failed");
      setOkMsg("Meter created.");
      setMeterForm((p) => ({ ...p, name: "", locationId: "", metaText: "" }));
      await loadMeters();
    } catch (e2) {
      setError(e2?.message || "Create meter failed");
    } finally {
      setBusyKey("");
    }
  }

  async function toggleMeterActive(meter) {
    setError("");
    setOkMsg("");

    const key = `toggle-meter-${meter.id}`;
    setBusyKey(key);

    try {
      const res = await updateMeter(meter.id, { isActive: !meter.is_active }).catch(() => null);
      if (!res?.ok) throw new Error(res?.message || res?.error || "Update failed");
      setOkMsg("Meter updated.");
      await loadMeters();
    } catch (e) {
      setError(e?.message || "Update failed");
    } finally {
      setBusyKey("");
    }
  }

  async function onOpenSession(e) {
    e.preventDefault();
    setError("");
    setOkMsg("");

    const payload = {
      meterId: Number(openForm.meterId),
      branchId: Number(openForm.branchId),
      startReading: openForm.startReading === "" ? null : Number(openForm.startReading),
      unitPrice: openForm.unitPrice === "" ? null : Number(openForm.unitPrice),
      taxRate: openForm.taxRate === "" ? null : Number(openForm.taxRate),
      notes: openForm.notes?.trim() ? openForm.notes.trim() : null,
    };

    const key = "open-session";
    setBusyKey(key);

    try {
      const res = await openSession(payload).catch(() => null);
      if (!res?.ok) throw new Error(res?.message || res?.error || "Open session failed");
      setOkMsg("Session opened.");
      setOpenForm((p) => ({ ...p, startReading: "", notes: "" }));
      await loadSessions();
    } catch (e2) {
      setError(e2?.message || "Open session failed");
    } finally {
      setBusyKey("");
    }
  }

  function sessionEditValue(id, field) {
    return editBySessionId[id]?.[field] ?? "";
  }

  function setSessionEdit(id, patch) {
    setEditBySessionId((p) => ({
      ...p,
      [id]: { ...(p[id] || {}), ...patch },
    }));
  }

  async function onRecordSession(id) {
    setError("");
    setOkMsg("");

    const key = `record-${id}`;
    setBusyKey(key);

    try {
      const payload = {
        endReading: sessionEditValue(id, "endReading") === "" ? null : Number(sessionEditValue(id, "endReading")),
        quantityDispensed:
          sessionEditValue(id, "quantityDispensed") === "" ? null : Number(sessionEditValue(id, "quantityDispensed")),
        unitPrice: sessionEditValue(id, "unitPrice") === "" ? null : Number(sessionEditValue(id, "unitPrice")),
        taxRate: sessionEditValue(id, "taxRate") === "" ? null : Number(sessionEditValue(id, "taxRate")),
      };

      const res = await recordSession(id, payload).catch(() => null);
      if (!res?.ok) throw new Error(res?.message || res?.error || "Record failed");
      setOkMsg("Session updated.");
      await loadSessions();
    } catch (e) {
      setError(e?.message || "Record failed");
    } finally {
      setBusyKey("");
    }
  }

  async function onCloseSession(id) {
    setError("");
    setOkMsg("");

    const key = `close-${id}`;
    setBusyKey(key);

    try {
      const payload = {
        endReading: sessionEditValue(id, "endReading") === "" ? null : Number(sessionEditValue(id, "endReading")),
        quantityDispensed:
          sessionEditValue(id, "quantityDispensed") === "" ? null : Number(sessionEditValue(id, "quantityDispensed")),
        unitPrice: sessionEditValue(id, "unitPrice") === "" ? null : Number(sessionEditValue(id, "unitPrice")),
        taxRate: sessionEditValue(id, "taxRate") === "" ? null : Number(sessionEditValue(id, "taxRate")),
      };

      const res = await closeSession(id, payload).catch(() => null);
      if (!res?.ok) throw new Error(res?.message || res?.error || "Close failed");
      setOkMsg("Session closed.");
      await loadSessions();
    } catch (e) {
      setError(e?.message || "Close failed");
    } finally {
      setBusyKey("");
    }
  }

  return (
    <section className="ucx-section">
      <div className="ucx-container ucx-admin-page">
        <div className="ucx-admin-header">
          <div>
            <h1 className="ucx-admin-title">Metered</h1>
            <p className="ucx-admin-subtitle">Manage meters and meter sessions.</p>
          </div>

          <div className="ucx-admin-header-right">
            {adminEmail ? <span className="ucx-small">Signed in as {adminEmail}</span> : null}
            <div className="ucx-admin-header-actions">
              <button type="button" className="ucx-btn ucx-btn-outline" onClick={refreshAll} disabled={loading}>
                {loading ? "Refreshing…" : "Refresh"}
              </button>
              <button type="button" className="ucx-admin-btn ucx-admin-btn-reject" onClick={onLogout}>
                Sign out
              </button>
            </div>
          </div>
        </div>

        {error ? <div className="ucx-admin-status ucx-admin-status-error">{error}</div> : null}
        {okMsg ? <div className="ucx-admin-status ucx-admin-status-success">{okMsg}</div> : null}

        {/* ===================== Meters ===================== */}
        <div className="ucx-admin-panel">
          <div className="ucx-admin-toolbar">
            <div>
              <div className="ucx-admin-panel-title" style={{ marginBottom: 2 }}>
                Meters
              </div>
              <div className="ucx-admin-toolbar-hint">
                List and manage registered meters. Use branchId/locationId as numeric IDs.
              </div>
            </div>

            <div className="ucx-admin-filters ucx-admin-filters-inventory">
              <input
                placeholder="Branch ID"
                value={meterFilters.branchId}
                onChange={(e) => setMeterFilters((p) => ({ ...p, branchId: e.target.value }))}
              />
              <select
                value={meterFilters.active}
                onChange={(e) => setMeterFilters((p) => ({ ...p, active: e.target.value }))}
              >
                <option value="">All</option>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
              <input
                placeholder="Type (fuel/water...)"
                value={meterFilters.type}
                onChange={(e) => setMeterFilters((p) => ({ ...p, type: e.target.value }))}
              />

              <button
                type="button"
                className="ucx-btn ucx-btn-outline"
                onClick={() => {
                  setError("");
                  setOkMsg("");
                  setLoading(true);
                  loadMeters()
                    .catch((e) => setError(e?.message || "Failed to load meters"))
                    .finally(() => setLoading(false));
                }}
                disabled={loading}
              >
                Apply
              </button>
            </div>
          </div>

          <form className="ucx-admin-form" onSubmit={onCreateMeter} style={{ marginTop: 12 }}>
            <div className="ucx-admin-form-grid ucx-admin-form-grid-3">
              <div className="ucx-admin-field">
                <label>Branch ID</label>
                <input
                  value={meterForm.branchId}
                  onChange={(e) => setMeterForm((p) => ({ ...p, branchId: e.target.value }))}
                  required
                />
              </div>
              <div className="ucx-admin-field">
                <label>Location ID (optional)</label>
                <input
                  value={meterForm.locationId}
                  onChange={(e) => setMeterForm((p) => ({ ...p, locationId: e.target.value }))}
                />
              </div>
              <div className="ucx-admin-field">
                <label>Name</label>
                <input
                  value={meterForm.name}
                  onChange={(e) => setMeterForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Pump 1"
                  required
                />
              </div>

              <div className="ucx-admin-field">
                <label>Type</label>
                <input value={meterForm.type} onChange={(e) => setMeterForm((p) => ({ ...p, type: e.target.value }))} />
              </div>
              <div className="ucx-admin-field">
                <label>UoM</label>
                <input value={meterForm.uom} onChange={(e) => setMeterForm((p) => ({ ...p, uom: e.target.value }))} />
              </div>
              <div className="ucx-admin-field ucx-admin-field-inline" style={{ marginTop: 24 }}>
                <input
                  type="checkbox"
                  checked={Boolean(meterForm.isActive)}
                  onChange={(e) => setMeterForm((p) => ({ ...p, isActive: e.target.checked }))}
                />
                <label style={{ margin: 0 }}>Active</label>
              </div>

              <div className="ucx-admin-field" style={{ gridColumn: "1 / -1" }}>
                <label>Meta (JSON, optional)</label>
                <input
                  value={meterForm.metaText}
                  onChange={(e) => setMeterForm((p) => ({ ...p, metaText: e.target.value }))}
                  placeholder='{"nozzle":"A"}'
                />
              </div>
            </div>

            <div className="ucx-admin-form-actions">
              <button type="submit" className="ucx-btn ucx-btn-primary" disabled={busyKey === "create-meter"}>
                {busyKey === "create-meter" ? "Creating…" : "Create Meter"}
              </button>
            </div>
          </form>

          <div className="ucx-admin-table-wrapper" style={{ marginTop: 14 }}>
            <table className="ucx-admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Branch</th>
                  <th>Location</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th>UoM</th>
                  <th>Active</th>
                  <th style={{ width: 180 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="ucx-admin-empty">
                      Loading…
                    </td>
                  </tr>
                ) : meters.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="ucx-admin-empty">
                      No meters found.
                    </td>
                  </tr>
                ) : (
                  meters.map((m) => {
                    const isBusy = busyKey === `toggle-meter-${m.id}`;
                    return (
                      <tr key={m.id}>
                        <td>{m.id}</td>
                        <td>{m.branch_id}</td>
                        <td>{m.location_id ?? "-"}</td>
                        <td>
                          <strong>{m.name}</strong>
                        </td>
                        <td>{m.type}</td>
                        <td>{m.uom}</td>
                        <td>{m.is_active ? "yes" : "no"}</td>
                        <td>
                          <button
                            type="button"
                            className="ucx-btn ucx-btn-outline"
                            onClick={() => toggleMeterActive(m)}
                            disabled={isBusy}
                          >
                            {isBusy ? "Working…" : m.is_active ? "Deactivate" : "Activate"}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ===================== Sessions ===================== */}
        <div className="ucx-admin-panel">
          <div className="ucx-admin-toolbar">
            <div>
              <div className="ucx-admin-panel-title" style={{ marginBottom: 2 }}>
                Sessions
              </div>
              <div className="ucx-admin-toolbar-hint">Open, record and close sessions (admin allowed).</div>
            </div>

            <div className="ucx-admin-filters ucx-admin-filters-inventory">
              <input
                placeholder="Branch ID"
                value={sessionFilters.branchId}
                onChange={(e) => setSessionFilters((p) => ({ ...p, branchId: e.target.value }))}
              />
              <input
                placeholder="Meter ID"
                value={sessionFilters.meterId}
                onChange={(e) => setSessionFilters((p) => ({ ...p, meterId: e.target.value }))}
              />
              <select
                value={sessionFilters.status}
                onChange={(e) => setSessionFilters((p) => ({ ...p, status: e.target.value }))}
              >
                <option value="">All</option>
                <option value="open">open</option>
                <option value="closed">closed</option>
                <option value="cancelled">cancelled</option>
              </select>

              <button
                type="button"
                className="ucx-btn ucx-btn-outline"
                onClick={() => {
                  setError("");
                  setOkMsg("");
                  setLoading(true);
                  loadSessions()
                    .catch((e) => setError(e?.message || "Failed to load sessions"))
                    .finally(() => setLoading(false));
                }}
                disabled={loading}
              >
                Apply
              </button>
            </div>
          </div>

          <form className="ucx-admin-form" onSubmit={onOpenSession} style={{ marginTop: 12 }}>
            <div className="ucx-admin-panel-title" style={{ marginBottom: 10 }}>
              Open a session
            </div>

            <div className="ucx-admin-form-grid ucx-admin-form-grid-3">
              <div className="ucx-admin-field">
                <label>Meter ID</label>
                <input
                  value={openForm.meterId}
                  onChange={(e) => setOpenForm((p) => ({ ...p, meterId: e.target.value }))}
                  required
                />
              </div>
              <div className="ucx-admin-field">
                <label>Branch ID</label>
                <input
                  value={openForm.branchId}
                  onChange={(e) => setOpenForm((p) => ({ ...p, branchId: e.target.value }))}
                  required
                />
              </div>
              <div className="ucx-admin-field">
                <label>Start reading (optional)</label>
                <input
                  value={openForm.startReading}
                  onChange={(e) => setOpenForm((p) => ({ ...p, startReading: e.target.value }))}
                />
              </div>

              <div className="ucx-admin-field">
                <label>Unit price (optional)</label>
                <input value={openForm.unitPrice} onChange={(e) => setOpenForm((p) => ({ ...p, unitPrice: e.target.value }))} />
              </div>
              <div className="ucx-admin-field">
                <label>Tax rate (optional, e.g. 0.15)</label>
                <input value={openForm.taxRate} onChange={(e) => setOpenForm((p) => ({ ...p, taxRate: e.target.value }))} />
              </div>
              <div className="ucx-admin-field">
                <label>Notes (optional)</label>
                <input value={openForm.notes} onChange={(e) => setOpenForm((p) => ({ ...p, notes: e.target.value }))} />
              </div>
            </div>

            <div className="ucx-admin-form-actions">
              <button type="submit" className="ucx-btn ucx-btn-primary" disabled={busyKey === "open-session"}>
                {busyKey === "open-session" ? "Opening…" : "Open Session"}
              </button>
            </div>
          </form>

          <div className="ucx-admin-table-wrapper" style={{ marginTop: 14 }}>
            <table className="ucx-admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Meter</th>
                  <th>Branch</th>
                  <th>Status</th>
                  <th>Opened</th>
                  <th>Closed</th>
                  <th>Qty</th>
                  <th>Unit</th>
                  <th>Total</th>
                  <th style={{ width: 320 }}>Record / Close</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={10} className="ucx-admin-empty">
                      Loading…
                    </td>
                  </tr>
                ) : sessions.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="ucx-admin-empty">
                      No sessions found.
                    </td>
                  </tr>
                ) : (
                  sessions.map((s) => {
                    const isOpen = s.status === "open";
                    const busyRecord = busyKey === `record-${s.id}`;
                    const busyClose = busyKey === `close-${s.id}`;

                    // Pre-fill meter/branch into open form convenience if blank
                    const meter = metersById.get(String(s.meter_id));

                    return (
                      <tr key={s.id}>
                        <td>{s.id}</td>
                        <td>
                          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            <strong>#{s.meter_id}</strong>
                            <span className="ucx-small" style={{ opacity: 0.85 }}>
                              {meter?.name ? meter.name : ""}
                            </span>
                          </div>
                        </td>
                        <td>{s.branch_id}</td>
                        <td>
                          <span className="ucx-badge">{s.status}</span>
                        </td>
                        <td>{fmtTs(s.opened_at)}</td>
                        <td>{fmtTs(s.closed_at)}</td>
                        <td>{s.quantity_dispensed ?? 0}</td>
                        <td>{s.unit_price ?? "-"}</td>
                        <td>{s.amount_total ?? "-"}</td>
                        <td>
                          {isOpen ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                                <input
                                  className="ucx-admin-input-compact"
                                  placeholder="Qty"
                                  value={sessionEditValue(s.id, "quantityDispensed")}
                                  onChange={(e) => setSessionEdit(s.id, { quantityDispensed: e.target.value })}
                                />
                                <input
                                  className="ucx-admin-input-compact"
                                  placeholder="End reading"
                                  value={sessionEditValue(s.id, "endReading")}
                                  onChange={(e) => setSessionEdit(s.id, { endReading: e.target.value })}
                                />
                                <input
                                  className="ucx-admin-input-compact"
                                  placeholder="Unit price"
                                  value={sessionEditValue(s.id, "unitPrice")}
                                  onChange={(e) => setSessionEdit(s.id, { unitPrice: e.target.value })}
                                />
                                <input
                                  className="ucx-admin-input-compact"
                                  placeholder="Tax rate"
                                  value={sessionEditValue(s.id, "taxRate")}
                                  onChange={(e) => setSessionEdit(s.id, { taxRate: e.target.value })}
                                />
                              </div>

                              <div className="ucx-admin-actions">
                                <button
                                  type="button"
                                  className="ucx-btn ucx-btn-outline"
                                  onClick={() => onRecordSession(s.id)}
                                  disabled={busyRecord || busyClose}
                                >
                                  {busyRecord ? "Working…" : "Record"}
                                </button>
                                <button
                                  type="button"
                                  className="ucx-admin-btn ucx-admin-btn-approve"
                                  onClick={() => onCloseSession(s.id)}
                                  disabled={busyClose || busyRecord}
                                >
                                  {busyClose ? "Working…" : "Close"}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <span className="ucx-small">No actions</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="ucx-admin-toolbar-hint" style={{ marginTop: 12 }}>
            Note: closing requires <code>quantity_dispensed &gt; 0</code> (per backend rules).
          </div>
        </div>
      </div>
    </section>
  );
}
